import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Full-size view of a learner's post screenshot. Closes on backdrop click or
 * Esc; locks page scroll while open and returns focus to the trigger.
 */
export default function StoryLightbox({ story, onClose }) {
  const { t } = useTranslation();
  const closeRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={story.name}
      className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center bg-[#0b0f19]/85 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[560px] my-auto bg-white rounded-[20px] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <p className="font-bold text-[#0b0f19] text-[15px] leading-tight truncate">{story.name}</p>
            <p className="text-primary font-bold tracking-widest text-[10px] uppercase mt-1 truncate">{story.tag}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("stories.close")}
            className="w-9 h-9 shrink-0 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <img src={story.image} alt={story.alt} width={900} height={story.height} className="w-full h-auto block" />
      </div>
    </div>
  );
}
